/** Diseñador Visual 2D — ventanas/puertas SA.
 * Genera datos estructurados (diseno: ventana2d) — el motor de costos/despiece calcula aparte.
 * Modelo: {tipo:'ventana2d', ancho_mm, alto_mm, cols_mm[], rows_mm[], modulos[{tipo_hoja, apertura}], version}
 */
import React, { useEffect, useState, useRef } from 'react';
import api from '@/api/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Plus, ArrowCounterClockwise, ArrowClockwise, MagnifyingGlassPlus, MagnifyingGlassMinus,
  CornersOut, ArrowsLeftRight, ArrowsDownUp, Trash, ArrowUUpLeft, Wrench,
} from '@phosphor-icons/react';
import { SIMBOLOS, renderSimbolo } from './simbolos/biblioteca';

export const disenoDefault = () => ({
  tipo: 'ventana2d', ancho_mm: 1500, alto_mm: 1500, cols_mm: [1500], rows_mm: [1500],
  modulos: [{ tipo_hoja: 'fijo', apertura: '' }], version: 0,
});

const TIPOS_HOJA = [
  ['fijo', 'Fijo'], ['corredizo', 'Corredizo'], ['abatible', 'Abatible'],
  ['oscilobatiente', 'Oscilobatiente'], ['proyeccion', 'Proyección'], ['otro', 'Otro'],
];
const APERTURAS = { corredizo: ['izquierda', 'derecha'], abatible: ['izquierda', 'derecha'], oscilobatiente: ['izquierda', 'derecha'], proyeccion: ['arriba', 'abajo'], otro: ['izquierda', 'derecha', 'arriba', 'abajo'] };

const norm = (d) => ({ ...disenoDefault(), ...(d || {}) });

/** Marcas gráficas de apertura dentro del módulo (x,y,w,h en coords SVG). */
const MarcaApertura = ({ m, x, y, w, h }) => {
  const cx = x + w / 2, cy = y + h / 2;
  const st = { stroke: '#0EA5E9', strokeWidth: 1.5, fill: 'none' };
  if (m.tipo_hoja === 'fijo') return null;
  if (m.tipo_hoja === 'corredizo') {
    const dir = m.apertura === 'derecha' ? 1 : -1;
    const a = w * 0.28;
    return (
      <g {...st}>
        <line x1={cx - a * dir} y1={cy} x2={cx + a * dir} y2={cy} />
        <polyline points={`${cx + a * dir - 7 * dir},${cy - 5} ${cx + a * dir},${cy} ${cx + a * dir - 7 * dir},${cy + 5}`} />
      </g>
    );
  }
  if (m.tipo_hoja === 'abatible' || m.tipo_hoja === 'oscilobatiente' || m.tipo_hoja === 'otro') {
    const bisagraIzq = m.apertura !== 'derecha';
    const x0 = bisagraIzq ? x : x + w, x1 = bisagraIzq ? x + w : x;
    return (
      <g {...st}>
        <polyline points={`${x1},${y} ${x0},${cy} ${x1},${y + h}`} />
        {m.tipo_hoja === 'oscilobatiente' && <polyline points={`${x},${y + h} ${cx},${y} ${x + w},${y + h}`} strokeDasharray="4 3" />}
      </g>
    );
  }
  if (m.tipo_hoja === 'proyeccion') {
    const arriba = m.apertura !== 'abajo';
    const yb = arriba ? y + h : y, yp = arriba ? y : y + h;
    return <polyline points={`${x},${yb} ${cx},${yp} ${x + w},${yb}`} {...st} />;
  }
  return null;
};

export const PreviewDiseno = ({ diseno, height = 110 }) => {
  const d = norm(diseno);
  const W = 200, H = height;
  const esc = Math.min((W - 20) / d.ancho_mm, (H - 14) / d.alto_mm);
  const w = d.ancho_mm * esc, h = d.alto_mm * esc, ox = (W - w) / 2, oy = (H - h) / 2;
  let acc = 0;
  const xs = d.cols_mm.map((c) => { const v = ox + acc * esc; acc += c; return [v, c * esc]; });
  acc = 0;
  const ys = d.rows_mm.map((r) => { const v = oy + acc * esc; acc += r; return [v, r * esc]; });
  return (
    <svg width={W} height={H} className="shrink-0" data-testid="diseno-preview">
      <rect x={ox} y={oy} width={w} height={h} fill="rgba(14,165,233,0.06)" stroke="var(--text-muted)" strokeWidth="3" />
      {ys.map(([y, rh], r) => xs.map(([x, cw], c) => {
        const m = d.modulos[r * d.cols_mm.length + c] || { tipo_hoja: 'fijo' };
        return (
          <g key={`${r}-${c}`}>
            <rect x={x} y={y} width={cw} height={rh} fill="none" stroke="var(--text-muted)" strokeWidth="1.2" />
            <MarcaApertura m={m} x={x + 3} y={y + 3} w={cw - 6} h={rh - 6} />
          </g>
        );
      }))}
      {(d.simbolos || []).map((s, i) => (
        <g key={i} transform={`translate(${ox + s.x * esc} ${oy + s.y * esc})`}>
          <g transform={`${s.orientacion === 'horizontal' ? 'rotate(90) ' : ''}scale(0.05)`}>{renderSimbolo(s.sid, '#B45309')}</g>
        </g>
      ))}
    </svg>
  );
};

export const Disenador2D = ({ value, onChange }) => {
  const d = norm(value);
  const [sel, setSel] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [selSim, setSelSim] = useState(null);
  const [showSimbolos, setShowSimbolos] = useState(false);
  const undoRef = useRef({ pila: [], idx: -1 });
  const dragRef = useRef(null);
  const [customSims, setCustomSims] = useState([]);
  useEffect(() => { api.get('/sa/simbolos').then((r) => setCustomSims(r.data.items || [])).catch(() => {}); }, []);

  const aplicar = (nuevo, registrar = true) => {
    if (registrar) {
      const u = undoRef.current;
      u.pila = u.pila.slice(0, u.idx + 1).concat([JSON.stringify(d)]).slice(-30);
      u.idx = u.pila.length - 1;
    }
    onChange({ ...nuevo });
  };
  const undo = () => {
    const u = undoRef.current;
    if (u.idx < 0) return;
    u.pila = u.pila.slice(0, u.idx + 1).concat([JSON.stringify(d)]);
    onChange(JSON.parse(u.pila[u.idx]));
    u.idx -= 1;
  };
  const redo = () => {
    const u = undoRef.current;
    if (u.idx + 2 > u.pila.length - 1) return;
    u.idx += 1;
    onChange(JSON.parse(u.pila[u.idx + 1]));
  };

  const nCols = d.cols_mm.length, nRows = d.rows_mm.length;
  const selR = Math.floor(sel / nCols), selC = sel % nCols;
  const modSel = d.modulos[sel] || { tipo_hoja: 'fijo', apertura: '' };

  const reModulos = (cols, rows, mapFn) => {
    const mods = [];
    for (let r = 0; r < rows.length; r++) for (let c = 0; c < cols.length; c++) mods.push(mapFn(r, c));
    return mods;
  };
  const getMod = (r, c) => d.modulos[r * nCols + c] || { tipo_hoja: 'fijo', apertura: '' };

  const setTotal = (campo, val) => {
    const v = Math.max(1, Number(val || 0));
    const key = campo === 'ancho_mm' ? 'cols_mm' : 'rows_mm';
    const suma = d[key].reduce((a, b) => a + b, 0) || 1;
    const escalado = d[key].map((x) => Math.round((x / suma) * v * 100) / 100);
    escalado[escalado.length - 1] = Math.round((v - escalado.slice(0, -1).reduce((a, b) => a + b, 0)) * 100) / 100;
    aplicar({ ...d, [campo]: v, [key]: escalado });
  };
  const setDiv = (key, i, val) => {
    const arr = [...d[key]];
    arr[i] = Number(val || 0);
    aplicar({ ...d, [key]: arr }, false);
  };
  const divV = () => {
    const cols = [...d.cols_mm];
    const mitad = Math.round((cols[selC] / 2) * 100) / 100;
    cols.splice(selC, 1, mitad, Math.round((d.cols_mm[selC] - mitad) * 100) / 100);
    const mods = reModulos(cols, d.rows_mm, (r, c) => getMod(r, c <= selC ? Math.min(c, selC) : c - 1));
    aplicar({ ...d, cols_mm: cols, modulos: mods });
  };
  const divH = () => {
    const rows = [...d.rows_mm];
    const mitad = Math.round((rows[selR] / 2) * 100) / 100;
    rows.splice(selR, 1, mitad, Math.round((d.rows_mm[selR] - mitad) * 100) / 100);
    const mods = reModulos(d.cols_mm, rows, (r, c) => getMod(r <= selR ? Math.min(r, selR) : r - 1, c));
    aplicar({ ...d, rows_mm: rows, modulos: mods });
  };
  const quitarDiv = () => {
    if (nCols > 1) {
      const cols = [...d.cols_mm];
      const j = Math.min(selC, nCols - 2);
      cols.splice(j, 2, Math.round((cols[j] + cols[j + 1]) * 100) / 100);
      const mods = reModulos(cols, d.rows_mm, (r, c) => getMod(r, c < j ? c : c + 1));
      aplicar({ ...d, cols_mm: cols, modulos: mods });
      setSel(0);
    } else if (nRows > 1) {
      const rows = [...d.rows_mm];
      const j = Math.min(selR, nRows - 2);
      rows.splice(j, 2, Math.round((rows[j] + rows[j + 1]) * 100) / 100);
      const mods = reModulos(d.cols_mm, rows, (r, c) => getMod(r < j ? r : r + 1, c));
      aplicar({ ...d, rows_mm: rows, modulos: mods });
      setSel(0);
    }
  };
  const setMod = (campo, val) => {
    const mods = [...d.modulos];
    mods[sel] = { ...modSel, [campo]: val };
    if (campo === 'tipo_hoja') mods[sel].apertura = (APERTURAS[val] || [''])[0] || '';
    aplicar({ ...d, modulos: mods });
  };

  // ── Símbolos (biblioteca Soluarq — anotaciones visuales, no afectan costos ni despiece) ──
  const simbolos = d.simbolos || [];
  const insertarSimbolo = (sid, nombre) => {
    const sims = [...simbolos, {
      sid, nombre, x: Math.round(d.ancho_mm / 2), y: Math.round(d.alto_mm / 2),
      etiqueta: `S${simbolos.length + 1}`, orientacion: 'vertical',
    }];
    aplicar({ ...d, simbolos: sims });
    setSelSim(sims.length - 1);
    setShowSimbolos(false);
  };
  const patchSimbolo = (i, patch, registrar = true) => {
    const sims = simbolos.map((s, j) => (j === i ? { ...s, ...patch } : s));
    aplicar({ ...d, simbolos: sims }, registrar);
  };
  const eliminarSimbolo = (i) => {
    aplicar({ ...d, simbolos: simbolos.filter((_, j) => j !== i) });
    setSelSim(null);
  };
  const startDragSim = (e, i) => {
    e.stopPropagation();
    setSelSim(i);
    dragRef.current = { i, sx: e.clientX, sy: e.clientY, x0: simbolos[i].x, y0: simbolos[i].y };
    e.target.setPointerCapture?.(e.pointerId);
  };
  const moveSim = (e) => {
    const g = dragRef.current;
    if (!g) return;
    patchSimbolo(g.i, {
      x: Math.round(g.x0 + (e.clientX - g.sx) / esc),
      y: Math.round(g.y0 + (e.clientY - g.sy) / esc),
    }, false);
  };
  const endDragSim = () => { dragRef.current = null; };

  // Edición directa sobre el dibujo: click en cota → nueva medida, compensando con el módulo vecino
  const editarCota = (key, i) => {
    const arr = d[key].map(Number);
    const val = window.prompt('Medida del módulo (mm):', Math.round(arr[i]));
    if (val === null) return;
    const v = Number(val);
    if (!v || v <= 0) return;
    if (arr.length === 1) {
      aplicar({ ...d, [key === 'cols_mm' ? 'ancho_mm' : 'alto_mm']: v, [key]: [v] });
      return;
    }
    const j = i < arr.length - 1 ? i + 1 : i - 1;
    const delta = v - arr[i];
    if (arr[j] - delta <= 0) return;
    const nuevo = [...arr];
    nuevo[i] = v;
    nuevo[j] = Math.round((arr[j] - delta) * 100) / 100;
    aplicar({ ...d, [key]: nuevo });
  };
  const editarTotal = (campo) => {
    const val = window.prompt(campo === 'ancho_mm' ? 'Ancho total (mm):' : 'Alto total (mm):', d[campo]);
    if (val === null) return;
    if (Number(val) > 0) setTotal(campo, val);
  };

  const sumaC = Math.round(d.cols_mm.reduce((a, b) => a + Number(b || 0), 0) * 100) / 100;
  const sumaR = Math.round(d.rows_mm.reduce((a, b) => a + Number(b || 0), 0) * 100) / 100;
  const okC = Math.abs(sumaC - d.ancho_mm) <= 0.51, okR = Math.abs(sumaR - d.alto_mm) <= 0.51;

  // render principal
  const W = 460 * zoom, H = 340 * zoom, M = 46;
  const esc = Math.min((W - M * 2) / d.ancho_mm, (H - M * 2) / d.alto_mm);
  const w = d.ancho_mm * esc, h = d.alto_mm * esc, ox = M + (W - M * 2 - w) / 2, oy = 14;
  let acc = 0;
  const xs = d.cols_mm.map((c) => { const v = ox + acc * esc; acc += Number(c || 0); return [v, Number(c || 0) * esc]; });
  acc = 0;
  const ys = d.rows_mm.map((r) => { const v = oy + acc * esc; acc += Number(r || 0); return [v, Number(r || 0) * esc]; });

  return (
    <div className="border border-[var(--border)] rounded-lg p-2.5 space-y-2" data-testid="disenador-2d">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1.5 items-center">
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={divV} data-testid="dz-div-v"><ArrowsLeftRight size={13} className="mr-1" />División V</Button>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={divH} data-testid="dz-div-h"><ArrowsDownUp size={13} className="mr-1" />División H</Button>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={quitarDiv} disabled={nCols === 1 && nRows === 1} data-testid="dz-quitar-div"><Trash size={13} className="mr-1" />Quitar división</Button>
        <span className="mx-1 w-px h-5 bg-[var(--border)]" />
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={undo} data-testid="dz-undo"><ArrowCounterClockwise size={14} /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={redo} data-testid="dz-redo"><ArrowClockwise size={14} /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => setZoom((z) => Math.min(2, z + 0.2))} data-testid="dz-zoom-in"><MagnifyingGlassPlus size={14} /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => setZoom((z) => Math.max(0.6, z - 0.2))} data-testid="dz-zoom-out"><MagnifyingGlassMinus size={14} /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => setZoom(1)} data-testid="dz-fit"><CornersOut size={14} /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-amber-500" onClick={() => { aplicar(disenoDefault()); setSel(0); setSelSim(null); }} data-testid="dz-reset"><ArrowUUpLeft size={14} className="mr-1" />Restablecer</Button>
        <span className="mx-1 w-px h-5 bg-[var(--border)]" />
        <Button type="button" variant="outline" size="sm" className={`h-7 text-xs ${showSimbolos ? 'border-amber-500 text-amber-600' : ''}`} onClick={() => setShowSimbolos((v) => !v)} data-testid="dz-simbolos-btn">
          <Wrench size={13} className="mr-1" />Símbolos{simbolos.length ? ` (${simbolos.length})` : ''}
        </Button>
      </div>

      {showSimbolos && (
        <div className="border border-dashed border-amber-500/60 rounded-lg p-2 space-y-1.5" data-testid="dz-sim-picker">
          <p className="text-[10px] font-bold text-[var(--text-muted)]">Biblioteca de símbolos Soluarq — clic para insertar en el diseño. Son anotaciones visuales: no afectan costos ni despiece. Luego arrástralos a su posición.</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(SIMBOLOS).filter(([, v]) => v.render).map(([sid, v]) => (
              <button key={sid} type="button" title={v.nombre} onClick={() => insertarSimbolo(sid, v.nombre)} data-testid={`dz-sim-add-${sid}`}
                className="w-16 border border-[var(--border)] rounded-md bg-[var(--card)] hover:border-amber-500 flex flex-col items-center py-1 gap-0.5">
                <svg width="34" height="28" viewBox="-175 -175 350 350">{renderSimbolo(sid, '#B45309')}</svg>
                <span className="text-[7px] leading-tight text-[var(--text-muted)] text-center px-0.5 line-clamp-2">{v.nombre}</span>
              </button>
            ))}
            {customSims.map((cs) => (
              <button key={cs.id} type="button" title={`${cs.nombre} (personalizado)`} data-testid={`dz-sim-add-custom-${cs.id}`}
                onClick={() => {
                  const sims = [...simbolos, { sid: 'custom', custom_path: cs.imagen_path, nombre: cs.nombre, x: Math.round(d.ancho_mm / 2), y: Math.round(d.alto_mm / 2), etiqueta: `S${simbolos.length + 1}`, orientacion: 'vertical' }];
                  aplicar({ ...d, simbolos: sims });
                  setSelSim(sims.length - 1);
                  setShowSimbolos(false);
                }}
                className="w-16 border border-emerald-500/50 rounded-md bg-[var(--card)] hover:border-emerald-500 flex flex-col items-center py-1 gap-0.5">
                <img src={`${api.defaults.baseURL}/sa/catalogo/imagen/${cs.imagen_path}`} alt={cs.nombre} className="w-[34px] h-[28px] object-contain" />
                <span className="text-[7px] leading-tight text-emerald-600 text-center px-0.5 line-clamp-2">{cs.nombre}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Medidas totales */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-[10px] font-bold text-[var(--text-muted)]">ANCHO (mm)</label>
          <Input type="number" value={d.ancho_mm} onChange={(e) => setTotal('ancho_mm', e.target.value)} className="h-8 w-24 text-xs" data-testid="dz-ancho" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-[var(--text-muted)]">ALTO (mm)</label>
          <Input type="number" value={d.alto_mm} onChange={(e) => setTotal('alto_mm', e.target.value)} className="h-8 w-24 text-xs" data-testid="dz-alto" />
        </div>
        {nCols > 1 && (
          <div>
            <label className={`text-[10px] font-bold ${okC ? 'text-[var(--text-muted)]' : 'text-red-500'}`}>ANCHOS DE MÓDULOS ({sumaC}/{d.ancho_mm} mm)</label>
            <div className="flex gap-1">
              {d.cols_mm.map((c, i) => (
                <Input key={i} type="number" value={c} onChange={(e) => setDiv('cols_mm', i, e.target.value)}
                  className={`h-8 w-20 text-xs ${okC ? '' : 'border-red-500'}`} data-testid={`dz-col-${i}`} />
              ))}
            </div>
          </div>
        )}
        {nRows > 1 && (
          <div>
            <label className={`text-[10px] font-bold ${okR ? 'text-[var(--text-muted)]' : 'text-red-500'}`}>ALTOS DE MÓDULOS ({sumaR}/{d.alto_mm} mm)</label>
            <div className="flex gap-1">
              {d.rows_mm.map((r, i) => (
                <Input key={i} type="number" value={r} onChange={(e) => setDiv('rows_mm', i, e.target.value)}
                  className={`h-8 w-20 text-xs ${okR ? '' : 'border-red-500'}`} data-testid={`dz-row-${i}`} />
              ))}
            </div>
          </div>
        )}
      </div>
      {(!okC || !okR) && (
        <p className="text-[11px] font-bold text-red-500" data-testid="dz-error-suma">
          ⚠ Las divisiones no coinciden con la medida total ({!okC ? `ancho ${sumaC} ≠ ${d.ancho_mm}` : ''}{!okC && !okR ? ' · ' : ''}{!okR ? `alto ${sumaR} ≠ ${d.alto_mm}` : ''}). Corrige antes de guardar.
        </p>
      )}

      {/* Canvas */}
      <div className="overflow-auto bg-[var(--bg)] rounded-lg flex justify-center">
        <svg width={W} height={H} data-testid="dz-canvas" onPointerMove={moveSim} onPointerUp={endDragSim} onPointerLeave={endDragSim}>
          <defs>
            <pattern id="dz-grid" width="18" height="18" patternUnits="userSpaceOnUse">
              <path d="M 18 0 L 0 0 0 18" fill="none" stroke="var(--border)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect x="0" y="0" width={W} height={H} fill="url(#dz-grid)" opacity="0.55" />
          <rect x={ox - 4} y={oy - 4} width={w + 8} height={h + 8} fill="rgba(14,165,233,0.05)" stroke="var(--text)" strokeWidth="5" />
          {ys.map(([y, rh], r) => xs.map(([x, cw], c) => {
            const idx = r * nCols + c;
            const m = d.modulos[idx] || { tipo_hoja: 'fijo' };
            const on = idx === sel;
            return (
              <g key={idx} onClick={() => setSel(idx)} style={{ cursor: 'pointer' }} data-testid={`dz-modulo-${idx}`}>
                <rect x={x} y={y} width={cw} height={rh} fill={on ? 'rgba(14,165,233,0.14)' : 'rgba(148,163,184,0.05)'}
                  stroke={on ? '#0EA5E9' : 'var(--text-muted)'} strokeWidth={on ? 2.5 : 1.5} />
                <MarcaApertura m={m} x={x + 6} y={y + 6} w={cw - 12} h={rh - 12} />
                <text x={x + cw / 2} y={y + rh / 2 - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={on ? '#0EA5E9' : 'var(--text-muted)'}>M{idx + 1}</text>
                <text x={x + cw / 2} y={y + rh / 2 + 10} textAnchor="middle" fontSize="9" fill="var(--text-muted)">
                  {Math.round(d.cols_mm[c])}×{Math.round(d.rows_mm[r])} · {(TIPOS_HOJA.find(([v]) => v === m.tipo_hoja) || ['', 'Fijo'])[1]}
                </text>
              </g>
            );
          }))}
          {/* Cotas inferiores por módulo + total */}
          {xs.map(([x, cw], c) => (
            <g key={`cx${c}`} stroke="var(--text-muted)" fill="var(--text-muted)">
              <line x1={x} y1={oy + h + 12} x2={x + cw} y2={oy + h + 12} strokeWidth="1" />
              <line x1={x} y1={oy + h + 8} x2={x} y2={oy + h + 16} strokeWidth="1" />
              <line x1={x + cw} y1={oy + h + 8} x2={x + cw} y2={oy + h + 16} strokeWidth="1" />
              <text x={x + cw / 2} y={oy + h + 24} textAnchor="middle" fontSize="10" stroke="none" fill="#0EA5E9"
                style={{ cursor: 'pointer' }} onClick={() => editarCota('cols_mm', c)} data-testid={`dz-cota-col-${c}`}>{Math.round(d.cols_mm[c])}</text>
            </g>
          ))}
          <text x={ox + w / 2} y={oy + h + 38} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--text)"
            style={{ cursor: 'pointer' }} onClick={() => editarTotal('ancho_mm')} data-testid="dz-cota-ancho">{d.ancho_mm} mm</text>
          {/* Cotas laterales por fila + total */}
          {ys.map(([y, rh], r) => (
            <g key={`cy${r}`} stroke="var(--text-muted)" fill="var(--text-muted)">
              <line x1={ox + w + 12} y1={y} x2={ox + w + 12} y2={y + rh} strokeWidth="1" />
              <line x1={ox + w + 8} y1={y} x2={ox + w + 16} y2={y} strokeWidth="1" />
              <line x1={ox + w + 8} y1={y + rh} x2={ox + w + 16} y2={y + rh} strokeWidth="1" />
              <text x={ox + w + 20} y={y + rh / 2 + 3} fontSize="10" stroke="none" fill="#0EA5E9"
                style={{ cursor: 'pointer' }} onClick={() => editarCota('rows_mm', r)} data-testid={`dz-cota-row-${r}`}>{Math.round(d.rows_mm[r])}</text>
            </g>
          ))}
          <text x={ox - 12} y={oy + h / 2} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--text)"
            style={{ cursor: 'pointer' }} onClick={() => editarTotal('alto_mm')} transform={`rotate(-90 ${ox - 12} ${oy + h / 2})`} data-testid="dz-cota-alto">{d.alto_mm} mm</text>
          {/* símbolos de la biblioteca — arrastrables */}
          {simbolos.map((s, i) => {
            const px = ox + s.x * esc, py = oy + s.y * esc;
            const on = selSim === i;
            return (
              <g key={i} transform={`translate(${px} ${py})`} style={{ cursor: 'grab' }} data-testid={`dz-sim-${i}`}
                onPointerDown={(e) => startDragSim(e, i)} onClick={(e) => { e.stopPropagation(); setSelSim(i); }}>
                <rect x={-22} y={-22} width={44} height={44} fill="transparent" pointerEvents="all" />
                {on && <circle r={24} fill="none" stroke="#0EA5E9" strokeWidth={1.5} strokeDasharray="4 3" />}
                <g transform={`${s.orientacion === 'horizontal' ? 'rotate(90) ' : ''}scale(${0.115 * zoom})`}>
                  {s.sid === 'custom' && s.custom_path
                    ? <image href={`${api.defaults.baseURL}/sa/catalogo/imagen/${s.custom_path}`} x={-150} y={-150} width={300} height={300} />
                    : renderSimbolo(s.sid, on ? '#0EA5E9' : '#B45309')}
                </g>
                {s.etiqueta && <text y={-27} textAnchor="middle" fontSize={10} fontWeight={800} fill={on ? '#0EA5E9' : '#B45309'}>{s.etiqueta}</text>}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Propiedades del módulo seleccionado */}
      <div className="flex flex-wrap gap-2 items-end border-t border-[var(--border)] pt-2" data-testid="dz-props">
        <span className="text-xs font-black text-[#0EA5E9]">Módulo M{sel + 1}</span>
        <span className="text-[11px] text-[var(--text-muted)]">{Math.round(d.cols_mm[selC])} × {Math.round(d.rows_mm[selR])} mm</span>
        <div>
          <label className="text-[10px] font-bold text-[var(--text-muted)]">Tipo de hoja</label>
          <select value={modSel.tipo_hoja} onChange={(e) => setMod('tipo_hoja', e.target.value)}
            className="block h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="dz-tipo-hoja">
            {TIPOS_HOJA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {(APERTURAS[modSel.tipo_hoja] || []).length > 0 && (
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)]">Apertura</label>
            <select value={modSel.apertura} onChange={(e) => setMod('apertura', e.target.value)}
              className="block h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="dz-apertura">
              {APERTURAS[modSel.tipo_hoja].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Propiedades del símbolo seleccionado */}
      {selSim != null && simbolos[selSim] && (
        <div className="flex flex-wrap gap-2 items-end border-t border-amber-500/40 pt-2" data-testid="dz-sim-props">
          <span className="text-xs font-black text-amber-600">Símbolo — {simbolos[selSim].nombre}</span>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)]">Etiqueta</label>
            <Input value={simbolos[selSim].etiqueta || ''} onChange={(e) => patchSimbolo(selSim, { etiqueta: e.target.value }, false)}
              className="h-8 w-16 text-xs" data-testid="dz-sim-etiqueta" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)]">Orientación</label>
            <select value={simbolos[selSim].orientacion || 'vertical'} onChange={(e) => patchSimbolo(selSim, { orientacion: e.target.value })}
              className="block h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="dz-sim-orient">
              <option value="vertical">Vertical</option><option value="horizontal">Horizontal</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)]">X (mm)</label>
            <Input type="number" value={simbolos[selSim].x} onChange={(e) => patchSimbolo(selSim, { x: Number(e.target.value) }, false)}
              className="h-8 w-20 text-xs" data-testid="dz-sim-x" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)]">Y (mm)</label>
            <Input type="number" value={simbolos[selSim].y} onChange={(e) => patchSimbolo(selSim, { y: Number(e.target.value) }, false)}
              className="h-8 w-20 text-xs" data-testid="dz-sim-y" />
          </div>
          <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] text-red-500 border-red-500/50 hover:bg-red-500/10"
            onClick={() => eliminarSimbolo(selSim)} data-testid="dz-sim-eliminar"><Trash size={13} className="mr-1" />Eliminar símbolo</Button>
        </div>
      )}
    </div>
  );
};
